import React, { useState, useEffect, useRef } from 'react';
import {
  Users,
  UserPlus,
  UserCheck,
  UserX,
  MessageSquare,
  Globe,
  Send,
  Search,
  Check,
  CheckCheck,
  Trash2,
  Shield,
  Clock,
  Sparkles,
  ArrowDown,
  Lock,
  Smile,
  X,
  Circle,
  Bell,
  RefreshCw,
} from 'lucide-react';
import { Socket } from 'socket.io-client';
import { BiblicalRankBadge } from './BiblicalRankBadge';
import { UserProfileModal } from './UserProfileModal';

export interface FriendItem {
  id: number;
  friendUid: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  country?: string;
  rankTier?: string;
  rating?: number;
  isOnline: boolean;
  lastActiveAt: string;
  unreadCount: number;
}

export interface FriendRequestItem {
  id: number;
  senderUid?: string;
  receiverUid?: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  country?: string;
  rankTier?: string;
  createdAt: string;
}

export interface PrivateMessageItem {
  id: number;
  conversationKey: string;
  senderUid: string;
  receiverUid: string;
  senderName: string;
  senderAvatar?: string;
  message: string;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
}

export interface GlobalChatMessageItem {
  id: number;
  senderUid: string;
  senderUsername: string;
  senderDisplayName: string;
  senderAvatar?: string;
  senderRankTier?: string;
  senderCountry?: string;
  message: string;
  createdAt: string;
}

interface FriendsAndChatViewProps {
  userUid: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  rankTier?: string;
  country?: string;
  isRegisteredAccount: boolean;
  isAdmin?: boolean;
  socket: Socket | null;
  initialTab?: 'friends' | 'global_chat';
  onSwitchEnvironment?: (env: 'friends' | 'global_chat') => void;
  onRequireAuth?: (mode: 'auth_signin' | 'auth_signup') => void;
  onBadgeCountChange?: (counts: { pendingRequests: number; unreadPrivate: number }) => void;
}

const QUICK_EMOJIS = ['🙏', '✝️', '🔥', '🙌', '📖', '👑', '🕊️', '💡', '👏', '❤️', '⚔️', '🌟'];

function formatRelativeTime(iso?: string): string {
  if (!iso) return 'Recently';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Recently';
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 45) return 'Just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatMessageTime(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export const FriendsAndChatView: React.FC<FriendsAndChatViewProps> = ({
  userUid,
  username,
  displayName,
  avatarUrl,
  rankTier = 'Seeker',
  country = 'Global',
  isRegisteredAccount,
  isAdmin = false,
  socket,
  initialTab = 'friends',
  onSwitchEnvironment,
  onRequireAuth,
  onBadgeCountChange,
}) => {
  const [activeEnvironment, setActiveEnvironment] = useState<'friends' | 'global_chat'>(initialTab);
  const [friendsSubTab, setFriendsSubTab] = useState<'my_friends' | 'requests' | 'find'>('my_friends');

  // Friends state
  const [friends, setFriends] = useState<FriendItem[]>([]);
  const [incomingRequests, setIncomingRequests] = useState<FriendRequestItem[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<FriendRequestItem[]>([]);
  const [loadingFriends, setLoadingFriends] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [actionStatus, setActionStatus] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Private Chat state (Strictly for accepted friends)
  const [selectedFriend, setSelectedFriend] = useState<FriendItem | null>(null);
  const [privateMessages, setPrivateMessages] = useState<PrivateMessageItem[]>([]);
  const [privateInput, setPrivateInput] = useState('');
  const [loadingPrivateChat, setLoadingPrivateChat] = useState(false);
  const [sendingPrivate, setSendingPrivate] = useState(false);
  const [showPrivateEmojis, setShowPrivateEmojis] = useState(false);

  // Global Chat state (Separate public community environment)
  const [globalMessages, setGlobalMessages] = useState<GlobalChatMessageItem[]>([]);
  const [globalInput, setGlobalInput] = useState('');
  const [onlineCount, setOnlineCount] = useState<number>(1);
  const [loadingGlobalChat, setLoadingGlobalChat] = useState(false);
  const [sendingGlobal, setSendingGlobal] = useState(false);
  const [showGlobalEmojis, setShowGlobalEmojis] = useState(false);
  const [unreadGlobalCount, setUnreadGlobalCount] = useState(0);
  const [isScrolledUpGlobal, setIsScrolledUpGlobal] = useState(false);

  // Profile modal
  const [inspectedUserUid, setInspectedUserUid] = useState<string | null>(null);

  const privateChatEndRef = useRef<HTMLDivElement>(null);
  const privateScrollContainerRef = useRef<HTMLDivElement>(null);
  const globalChatEndRef = useRef<HTMLDivElement>(null);
  const globalScrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollPrivateContainerToBottom = (smooth = false) => {
    const el = privateScrollContainerRef.current;
    if (!el) return;
    if (smooth && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  };

  const scrollGlobalContainerToBottom = (smooth = false) => {
    const el = globalScrollContainerRef.current;
    if (!el) return;
    if (smooth && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  };

  useEffect(() => {
    setActiveEnvironment(initialTab);
  }, [initialTab]);

  const showToast = (text: string, type: 'success' | 'error' = 'success') => {
    setActionStatus({ text, type });
    setTimeout(() => setActionStatus(null), 3500);
  };

  // Load Friends Overview
  const fetchFriendsOverview = async () => {
    if (!userUid || !isRegisteredAccount) return;
    setLoadingFriends(true);
    try {
      const res = await fetch(`/api/friends?userUid=${encodeURIComponent(userUid)}`);
      const data = await res.json();
      if (data.success) {
        setFriends(data.friends || []);
        setIncomingRequests(data.incomingRequests || []);
        setOutgoingRequests(data.outgoingRequests || []);
        const totalUnread = (data.friends || []).reduce((acc: number, f: FriendItem) => acc + (f.unreadCount || 0), 0);
        onBadgeCountChange?.({
          pendingRequests: (data.incomingRequests || []).length,
          unreadPrivate: totalUnread,
        });
      }
    } catch (e) {
      console.error('Failed to load friends overview:', e);
    } finally {
      setLoadingFriends(false);
    }
  };

  // Load Global Chat
  const fetchGlobalChat = async () => {
    setLoadingGlobalChat(true);
    try {
      const res = await fetch('/api/chat/global?limit=80');
      const data = await res.json();
      if (data.success) {
        setGlobalMessages(data.messages || []);
        if (typeof data.onlineCount === 'number') {
          setOnlineCount(Math.max(1, data.onlineCount));
        }
      }
    } catch (e) {
      console.error('Failed to load global chat:', e);
    } finally {
      setLoadingGlobalChat(false);
    }
  };

  // Load Private Conversation with selected friend
  const fetchPrivateConversation = async (friendUid: string) => {
    if (!userUid || !friendUid) return;
    setLoadingPrivateChat(true);
    try {
      const res = await fetch(`/api/messages/private/${encodeURIComponent(friendUid)}?userUid=${encodeURIComponent(userUid)}`);
      const data = await res.json();
      if (data.success) {
        setPrivateMessages(data.messages || []);
        // Clear unread count locally for this friend
        setFriends((prev) =>
          prev.map((f) => (f.friendUid === friendUid ? { ...f, unreadCount: 0 } : f))
        );
        socket?.emit('dm:read', { userUid, friendUid });
      } else if (data.error) {
        showToast(data.error, 'error');
      }
    } catch (e) {
      console.error('Failed to load private messages:', e);
    } finally {
      setLoadingPrivateChat(false);
    }
  };

  useEffect(() => {
    fetchFriendsOverview();
    fetchGlobalChat();
    const interval = setInterval(() => {
      if (isRegisteredAccount) fetchFriendsOverview();
    }, 25000);
    return () => clearInterval(interval);
  }, [userUid, isRegisteredAccount]);

  useEffect(() => {
    if (selectedFriend) {
      fetchPrivateConversation(selectedFriend.friendUid);
    }
  }, [selectedFriend?.friendUid]);

  // Auto-scroll private chat container to bottom without scrolling the outer document window
  useEffect(() => {
    scrollPrivateContainerToBottom(false);
  }, [privateMessages.length, selectedFriend?.friendUid]);

  // Auto-scroll global chat container when at bottom without scrolling the outer document window
  useEffect(() => {
    if (activeEnvironment === 'global_chat' && !isScrolledUpGlobal) {
      scrollGlobalContainerToBottom(false);
      setUnreadGlobalCount(0);
    }
  }, [globalMessages.length, activeEnvironment]);

  // Socket.IO Real-Time Listeners
  useEffect(() => {
    if (!socket) return;

    const handleIncomingDm = (msg: PrivateMessageItem) => {
      const isCurrentChat =
        selectedFriend &&
        ((msg.senderUid === selectedFriend.friendUid && msg.receiverUid === userUid) ||
          (msg.senderUid === userUid && msg.receiverUid === selectedFriend.friendUid));

      if (isCurrentChat) {
        setPrivateMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        if (msg.receiverUid === userUid && selectedFriend) {
          fetch('/api/messages/private/read', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userUid, friendUid: selectedFriend.friendUid }),
          }).catch(() => {});
          socket.emit('dm:read', { userUid, friendUid: selectedFriend.friendUid });
        }
      } else if (msg.receiverUid === userUid) {
        setFriends((prev) =>
          prev.map((f) =>
            f.friendUid === msg.senderUid
              ? { ...f, unreadCount: (f.unreadCount || 0) + 1, isOnline: true }
              : f
          )
        );
      }
    };

    const handleDmReadReceipt = (payload: { readerUid: string; friendUid: string }) => {
      if (selectedFriend && payload.readerUid === selectedFriend.friendUid) {
        setPrivateMessages((prev) =>
          prev.map((m) => (m.senderUid === userUid ? { ...m, isRead: true } : m))
        );
      }
    };

    const handleGlobalMessage = (msg: GlobalChatMessageItem) => {
      setGlobalMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      if (activeEnvironment !== 'global_chat' || isScrolledUpGlobal) {
        if (msg.senderUid !== userUid) {
          setUnreadGlobalCount((c) => c + 1);
        }
      }
    };

    const handleGlobalDelete = (payload: { messageId: number }) => {
      setGlobalMessages((prev) => prev.filter((m) => m.id !== payload.messageId));
    };

    const handleOnlineCount = (payload: { onlineCount: number }) => {
      if (typeof payload?.onlineCount === 'number') {
        setOnlineCount(Math.max(1, payload.onlineCount));
      }
    };

    socket.on('dm:message', handleIncomingDm);
    socket.on('dm:read_receipt', handleDmReadReceipt);
    socket.on('global_chat:message', handleGlobalMessage);
    socket.on('global_chat:delete', handleGlobalDelete);
    socket.on('presence:online_count', handleOnlineCount);

    return () => {
      socket.off('dm:message', handleIncomingDm);
      socket.off('dm:read_receipt', handleDmReadReceipt);
      socket.off('global_chat:message', handleGlobalMessage);
      socket.off('global_chat:delete', handleGlobalDelete);
      socket.off('presence:online_count', handleOnlineCount);
    };
  }, [socket, selectedFriend?.friendUid, userUid, activeEnvironment, isScrolledUpGlobal]);

  // Search Registered Users
  const handleSearchUsers = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setSearching(true);
    try {
      const res = await fetch(
        `/api/friends/search?q=${encodeURIComponent(q)}&viewerUid=${encodeURIComponent(userUid)}`
      );
      const data = await res.json();
      if (data.success) {
        setSearchResults(data.users || []);
      }
    } catch (e) {
      console.error('User search error:', e);
    } finally {
      setSearching(false);
    }
  };

  const handleSendFriendRequest = async (receiverUid: string) => {
    try {
      const res = await fetch('/api/friends/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senderUid: userUid, receiverUid }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Friend request sent!', 'success');
        setSearchResults((prev) =>
          prev.map((u) => (u.uid === receiverUid ? { ...u, friendshipStatus: 'pending_outgoing' } : u))
        );
        fetchFriendsOverview();
      } else {
        showToast(data.error || 'Could not send friend request.', 'error');
      }
    } catch (e) {
      showToast('Failed to send friend request.', 'error');
    }
  };

  const handleCancelRequest = async (receiverUid: string) => {
    try {
      const res = await fetch('/api/friends/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senderUid: userUid, receiverUid }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Friend request cancelled.', 'success');
        fetchFriendsOverview();
        setSearchResults((prev) =>
          prev.map((u) => (u.uid === receiverUid ? { ...u, friendshipStatus: 'none' } : u))
        );
      }
    } catch (e) {
      showToast('Could not cancel request.', 'error');
    }
  };

  const handleRespondRequest = async (requestId: number, accept: boolean) => {
    try {
      const res = await fetch('/api/friends/respond', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId, receiverUid: userUid, accept }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(accept ? 'Friend request accepted!' : 'Friend request declined.', 'success');
        fetchFriendsOverview();
      } else {
        showToast(data.error || 'Could not respond to request.', 'error');
      }
    } catch (e) {
      showToast('Error responding to friend request.', 'error');
    }
  };

  const handleRemoveFriend = async (friendUid: string, friendName: string) => {
    try {
      const res = await fetch('/api/friends/unfriend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userUid, friendUid }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(`Removed ${friendName} from your friends list.`, 'success');
        if (selectedFriend?.friendUid === friendUid) {
          setSelectedFriend(null);
          setPrivateMessages([]);
        }
        fetchFriendsOverview();
      }
    } catch (e) {
      showToast('Failed to remove friend.', 'error');
    }
  };

  // Send Private Message (Backend enforces accepted friendship)
  const handleSendPrivateMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFriend || !privateInput.trim() || sendingPrivate) return;
    const text = privateInput.trim();
    setPrivateInput('');
    setShowPrivateEmojis(false);
    setSendingPrivate(true);

    try {
      const res = await fetch('/api/messages/private/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          senderUid: userUid,
          receiverUid: selectedFriend.friendUid,
          message: text,
        }),
      });
      const data = await res.json();
      if (data.success && data.message) {
        setPrivateMessages((prev) => {
          if (prev.some((m) => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
      } else {
        showToast(data.error || 'Could not send private message.', 'error');
      }
    } catch (e) {
      showToast('Failed to deliver message.', 'error');
    } finally {
      setSendingPrivate(false);
    }
  };

  // Send Global Chat Message
  const handleSendGlobalMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isRegisteredAccount) {
      onRequireAuth?.('auth_signin');
      return;
    }
    const text = globalInput.trim();
    if (!text || sendingGlobal) return;
    setGlobalInput('');
    setShowGlobalEmojis(false);
    setSendingGlobal(true);

    try {
      const res = await fetch('/api/chat/global/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          senderUid: userUid,
          senderUsername: username || displayName,
          senderDisplayName: displayName || username,
          senderAvatar: avatarUrl,
          senderRankTier: rankTier,
          senderCountry: country,
          message: text,
        }),
      });
      const data = await res.json();
      if (data.success && data.message) {
        setGlobalMessages((prev) => {
          if (prev.some((m) => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
        setIsScrolledUpGlobal(false);
        setTimeout(() => scrollGlobalContainerToBottom(true), 50);
      } else {
        showToast(data.error || 'Unable to post message.', 'error');
      }
    } catch (e) {
      showToast('Network error while posting message.', 'error');
    } finally {
      setSendingGlobal(false);
    }
  };

  // Admin Delete Global Message
  const handleDeleteGlobalMessage = async (msgId: number) => {
    try {
      const res = await fetch(`/api/chat/global/${msgId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actorUid: userUid, isAdmin }),
      });
      const data = await res.json();
      if (data.success) {
        setGlobalMessages((prev) => prev.filter((m) => m.id !== msgId));
        showToast('Message removed.', 'success');
      } else {
        showToast(data.error || 'Failed to delete message.', 'error');
      }
    } catch (e) {
      showToast('Failed to delete message.', 'error');
    }
  };

  const totalUnreadPrivate = friends.reduce((acc, f) => acc + (f.unreadCount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Top Environment Switcher — Keeps My Friends & Private Chat distinct from Global Chat */}
      <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-widest mb-1">
            <Sparkles className="w-3.5 h-3.5" /> Quick!Bible Social Hub
          </div>
          <h2 className="text-xl sm:text-2xl font-serif font-bold text-white">
            {activeEnvironment === 'friends'
              ? 'My Friends & Private Messenger'
              : 'Global Community Chat'}
          </h2>
          <p className="text-xs sm:text-sm text-slate-400">
            {activeEnvironment === 'friends'
              ? 'Connect with fellow Bible scholars and chat 1-on-1 in encrypted friend-only conversations.'
              : 'Real-time public fellowship room for all registered Quick!Bible players worldwide.'}
          </p>
        </div>

        <div className="flex items-center gap-2 bg-slate-950 p-1.5 rounded-xl border border-slate-800 self-start sm:self-auto">
          <button
            onClick={() => {
              setActiveEnvironment('friends');
              onSwitchEnvironment?.('friends');
            }}
            className={`relative flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs sm:text-sm font-bold transition-all ${
              activeEnvironment === 'friends'
                ? 'bg-amber-500 text-slate-950 shadow-lg shadow-amber-500/20'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>My Friends</span>
            {(incomingRequests.length > 0 || totalUnreadPrivate > 0) && (
              <span className="px-1.5 py-0.5 text-[10px] font-black rounded-full bg-rose-500 text-white">
                {incomingRequests.length + totalUnreadPrivate}
              </span>
            )}
          </button>

          <button
            onClick={() => {
              setActiveEnvironment('global_chat');
              setUnreadGlobalCount(0);
              onSwitchEnvironment?.('global_chat');
            }}
            className={`relative flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs sm:text-sm font-bold transition-all ${
              activeEnvironment === 'global_chat'
                ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Globe className="w-4 h-4" />
            <span>Global Chat</span>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {onlineCount}
            </span>
            {unreadGlobalCount > 0 && activeEnvironment !== 'global_chat' && (
              <span className="px-1.5 py-0.5 text-[10px] font-black rounded-full bg-amber-500 text-slate-950">
                {unreadGlobalCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Toast Notification */}
      {actionStatus && (
        <div
          className={`px-4 py-3 rounded-xl border text-xs sm:text-sm font-semibold flex items-center justify-between ${
            actionStatus.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
              : 'bg-rose-500/10 border-rose-500/30 text-rose-300'
          }`}
        >
          <span>{actionStatus.text}</span>
          <button onClick={() => setActionStatus(null)} className="text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ENVIRONMENT 1: MY FRIENDS & PRIVATE MESSAGING */}
      {activeEnvironment === 'friends' && (
        <>
          {!isRegisteredAccount ? (
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-8 text-center max-w-xl mx-auto my-6">
              <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-4 text-amber-400">
                <Lock className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-serif font-bold text-white mb-2">
                Sign In to Access My Friends & Private Chat
              </h3>
              <p className="text-sm text-slate-400 mb-6">
                Create a free Quick!Bible account to search registered players, send friend requests, see online status, and send private messages to your friends.
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => onRequireAuth?.('auth_signup')}
                  className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm transition-all"
                >
                  Create Free Account
                </button>
                <button
                  onClick={() => onRequireAuth?.('auth_signin')}
                  className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm border border-slate-700 transition-all"
                >
                  Sign In
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left Column: Friends List, Requests & Search (5 cols) */}
              <div className="lg:col-span-5 bg-slate-900/90 border border-slate-800 rounded-2xl flex flex-col overflow-hidden h-[640px]">
                {/* Friends Sub-Navigation */}
                <div className="p-3 bg-slate-950/70 border-b border-slate-800 flex items-center justify-between gap-1">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setFriendsSubTab('my_friends')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                        friendsSubTab === 'my_friends'
                          ? 'bg-slate-800 text-amber-400 border border-amber-500/30'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      Friends ({friends.length})
                    </button>
                    <button
                      onClick={() => setFriendsSubTab('requests')}
                      className={`relative px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                        friendsSubTab === 'requests'
                          ? 'bg-slate-800 text-amber-400 border border-amber-500/30'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      <span>Requests</span>
                      {incomingRequests.length > 0 && (
                        <span className="px-1.5 py-0.2 bg-rose-500 text-white rounded-full text-[10px] font-black">
                          {incomingRequests.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setFriendsSubTab('find')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${
                        friendsSubTab === 'find'
                          ? 'bg-slate-800 text-amber-400 border border-amber-500/30'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      <span>Find Players</span>
                    </button>
                  </div>

                  <button
                    onClick={fetchFriendsOverview}
                    title="Refresh Friends"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${loadingFriends ? 'animate-spin text-amber-400' : ''}`} />
                  </button>
                </div>

                {/* Sub-Tab 1: My Friends List */}
                {friendsSubTab === 'my_friends' && (
                  <div className="flex-1 overflow-y-auto divide-y divide-slate-800/60">
                    {friends.length === 0 ? (
                      <div className="p-8 text-center">
                        <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                        <p className="text-sm font-semibold text-slate-300 mb-1">No Friends Added Yet</p>
                        <p className="text-xs text-slate-500 mb-4">
                          Search registered players by username or display name to add friends and start private chats.
                        </p>
                        <button
                          onClick={() => setFriendsSubTab('find')}
                          className="px-4 py-2 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-slate-950 text-xs font-bold transition-all"
                        >
                          Find Friends
                        </button>
                      </div>
                    ) : (
                      friends.map((friend) => {
                        const isSelected = selectedFriend?.friendUid === friend.friendUid;
                        return (
                          <div
                            key={friend.friendUid}
                            className={`p-3.5 flex items-center justify-between gap-3 transition-colors cursor-pointer ${
                              isSelected ? 'bg-amber-500/10 border-l-2 border-l-amber-400' : 'hover:bg-slate-800/50'
                            }`}
                            onClick={() => setSelectedFriend(friend)}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="relative shrink-0">
                                <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden text-sm font-bold text-amber-400">
                                  {friend.avatarUrl ? (
                                    <img src={friend.avatarUrl} alt={friend.displayName} className="w-full h-full object-cover" />
                                  ) : (
                                    (friend.displayName || friend.username || 'S').charAt(0).toUpperCase()
                                  )}
                                </div>
                                <span
                                  title={friend.isOnline ? 'Online Now' : `Last active ${formatRelativeTime(friend.lastActiveAt)}`}
                                  className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-slate-900 ${
                                    friend.isOnline ? 'bg-emerald-400' : 'bg-slate-500'
                                  }`}
                                />
                              </div>

                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm font-bold text-white truncate">
                                    {friend.displayName || friend.username}
                                  </span>
                                  {friend.unreadCount > 0 && (
                                    <span className="px-1.5 py-0.2 rounded-full bg-amber-500 text-slate-950 text-[10px] font-black">
                                      {friend.unreadCount} new
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-[11px] text-slate-400">
                                  <span>@{friend.username}</span>
                                  <span>•</span>
                                  <span className={friend.isOnline ? 'text-emerald-400 font-medium' : 'text-slate-500'}>
                                    {friend.isOnline ? 'Online' : `Active ${formatRelativeTime(friend.lastActiveAt)}`}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => setSelectedFriend(friend)}
                                title="Open Private Chat"
                                className="p-2 rounded-lg bg-slate-800 hover:bg-amber-500 hover:text-slate-950 text-slate-300 transition-colors"
                              >
                                <MessageSquare className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setInspectedUserUid(friend.friendUid)}
                                title="View Profile"
                                className="px-2 py-1 rounded-lg bg-slate-800/80 hover:bg-slate-700 text-[10px] font-bold text-slate-300"
                              >
                                Profile
                              </button>
                              <button
                                onClick={() => handleRemoveFriend(friend.friendUid, friend.displayName || friend.username)}
                                title="Remove Friend"
                                className="p-2 rounded-lg bg-slate-800/60 hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 transition-colors"
                              >
                                <UserX className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {/* Sub-Tab 2: Incoming & Outgoing Friend Requests */}
                {friendsSubTab === 'requests' && (
                  <div className="flex-1 overflow-y-auto p-4 space-y-5">
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-amber-400 mb-2.5 flex items-center gap-1.5">
                        <Bell className="w-3.5 h-3.5" /> Incoming Requests ({incomingRequests.length})
                      </h4>
                      {incomingRequests.length === 0 ? (
                        <p className="text-xs text-slate-500 bg-slate-950/50 border border-slate-800/80 rounded-xl p-3">
                          No incoming friend requests right now.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {incomingRequests.map((req) => (
                            <div
                              key={req.id}
                              className="p-3 rounded-xl bg-slate-950/80 border border-slate-800 flex items-center justify-between gap-2"
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-white truncate">
                                  {req.displayName || req.username}
                                </p>
                                <p className="text-[11px] text-slate-400">
                                  @{req.username} • {formatRelativeTime(req.createdAt)}
                                </p>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <button
                                  onClick={() => handleRespondRequest(req.id, true)}
                                  className="px-2.5 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold flex items-center gap-1"
                                >
                                  <Check className="w-3.5 h-3.5" /> Accept
                                </button>
                                <button
                                  onClick={() => handleRespondRequest(req.id, false)}
                                  className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-rose-500/20 text-slate-300 hover:text-rose-300 text-xs font-semibold"
                                >
                                  Decline
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" /> Sent Pending Requests ({outgoingRequests.length})
                      </h4>
                      {outgoingRequests.length === 0 ? (
                        <p className="text-xs text-slate-500 bg-slate-950/50 border border-slate-800/80 rounded-xl p-3">
                          No pending sent requests.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {outgoingRequests.map((req) => (
                            <div
                              key={req.id}
                              className="p-3 rounded-xl bg-slate-950/80 border border-slate-800 flex items-center justify-between gap-2"
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-white truncate">
                                  {req.displayName || req.username}
                                </p>
                                <p className="text-[11px] text-slate-400">@{req.username} • Pending approval</p>
                              </div>
                              {req.receiverUid && (
                                <button
                                  onClick={() => handleCancelRequest(req.receiverUid!)}
                                  className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-rose-500/20 text-slate-300 hover:text-rose-300 text-xs font-semibold shrink-0"
                                >
                                  Cancel
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Sub-Tab 3: Find Registered Users */}
                {friendsSubTab === 'find' && (
                  <div className="flex-1 flex flex-col overflow-hidden p-4">
                    <form onSubmit={handleSearchUsers} className="flex gap-2 mb-4">
                      <div className="relative flex-1">
                        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search by username or display name..."
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-3 py-2 text-xs sm:text-sm text-white focus:outline-none focus:border-amber-500"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={searching}
                        className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs sm:text-sm transition-all"
                      >
                        {searching ? '...' : 'Search'}
                      </button>
                    </form>

                    <div className="flex-1 overflow-y-auto space-y-2">
                      {searchResults.length === 0 ? (
                        <p className="text-xs text-slate-500 text-center py-8">
                          Enter a username or display name above to find registered Quick!Bible players.
                        </p>
                      ) : (
                        searchResults.map((u) => (
                          <div
                            key={u.uid}
                            className="p-3 rounded-xl bg-slate-950/90 border border-slate-800 flex items-center justify-between gap-2"
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="relative w-9 h-9 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-bold text-amber-400 shrink-0">
                                {(u.displayName || u.username || 'S').charAt(0).toUpperCase()}
                                <span
                                  className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-slate-950 ${
                                    u.isOnline ? 'bg-emerald-400' : 'bg-slate-600'
                                  }`}
                                />
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs sm:text-sm font-bold text-white truncate">
                                  {u.displayName || u.username}
                                </p>
                                <p className="text-[11px] text-slate-400 truncate">
                                  @{u.username} • {u.rankTier || 'Seeker'}
                                </p>
                              </div>
                            </div>

                            <div className="shrink-0">
                              {u.friendshipStatus === 'accepted' ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/30">
                                  <UserCheck className="w-3.5 h-3.5" /> Friends
                                </span>
                              ) : u.friendshipStatus === 'pending_outgoing' ? (
                                <button
                                  onClick={() => handleCancelRequest(u.uid)}
                                  className="px-2.5 py-1 rounded-lg bg-slate-800 text-slate-300 hover:text-rose-300 text-xs font-semibold"
                                >
                                  Cancel Request
                                </button>
                              ) : u.friendshipStatus === 'pending_incoming' ? (
                                <button
                                  onClick={() => setFriendsSubTab('requests')}
                                  className="px-2.5 py-1 rounded-lg bg-amber-500/20 text-amber-300 text-xs font-bold"
                                >
                                  Respond
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleSendFriendRequest(u.uid)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold transition-all"
                                >
                                  <UserPlus className="w-3.5 h-3.5" /> Add Friend
                                </button>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Private 1-on-1 Friend Messenger (7 cols) */}
              <div className="lg:col-span-7 bg-slate-900/90 border border-slate-800 rounded-2xl flex flex-col overflow-hidden h-[640px]">
                {!selectedFriend ? (
                  <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 mb-4">
                      <MessageSquare className="w-7 h-7" />
                    </div>
                    <h3 className="text-lg font-serif font-bold text-white mb-1">
                      Private Friends Messenger
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-400 max-w-md">
                      Select an accepted friend from your list on the left to open your private conversation. Only mutually accepted friends can exchange direct messages.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Conversation Header */}
                    <div className="p-4 bg-slate-950/80 border-b border-slate-800 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="relative">
                          <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-sm font-bold text-amber-400 overflow-hidden">
                            {selectedFriend.avatarUrl ? (
                              <img
                                src={selectedFriend.avatarUrl}
                                alt={selectedFriend.displayName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              (selectedFriend.displayName || selectedFriend.username || 'S').charAt(0).toUpperCase()
                            )}
                          </div>
                          <span
                            className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-slate-950 ${
                              selectedFriend.isOnline ? 'bg-emerald-400' : 'bg-slate-500'
                            }`}
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-bold text-white truncate">
                              {selectedFriend.displayName || selectedFriend.username}
                            </h4>
                            <BiblicalRankBadge rankName={selectedFriend.rankTier || 'Seeker'} size="sm" showLabel />
                          </div>
                          <p className="text-[11px] text-slate-400">
                            {selectedFriend.isOnline
                              ? 'Online now • Friends-Only Encrypted Chat'
                              : `Last active ${formatRelativeTime(selectedFriend.lastActiveAt)}`}
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={() => setInspectedUserUid(selectedFriend.friendUid)}
                        className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 border border-slate-700"
                      >
                        View Profile
                      </button>
                    </div>

                    {/* Messages Area */}
                    <div
                      ref={privateScrollContainerRef}
                      className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-950/30"
                    >
                      {loadingPrivateChat ? (
                        <div className="text-center py-12 text-xs text-slate-500">Loading conversation...</div>
                      ) : privateMessages.length === 0 ? (
                        <div className="text-center py-12">
                          <p className="text-xs text-slate-400 font-medium mb-1">
                            No messages with {selectedFriend.displayName || selectedFriend.username} yet.
                          </p>
                          <p className="text-[11px] text-slate-500">
                            Send a greeting or challenge them to a Scripture match!
                          </p>
                        </div>
                      ) : (
                        privateMessages.map((m) => {
                          const isMine = m.senderUid === userUid;
                          return (
                            <div
                              key={m.id}
                              className={`flex items-end gap-2 ${isMine ? 'justify-end' : 'justify-start'}`}
                            >
                              {!isMine && (
                                <div className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-bold text-amber-400 shrink-0 overflow-hidden">
                                  {selectedFriend.avatarUrl ? (
                                    <img
                                      src={selectedFriend.avatarUrl}
                                      alt={selectedFriend.displayName}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    (selectedFriend.displayName || selectedFriend.username || 'S')
                                      .charAt(0)
                                      .toUpperCase()
                                  )}
                                </div>
                              )}
                              <div
                                className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-xs sm:text-sm ${
                                  isMine
                                    ? 'bg-amber-500 text-slate-950 font-medium rounded-br-sm'
                                    : 'bg-slate-800 text-slate-100 border border-slate-700/80 rounded-bl-sm'
                                }`}
                              >
                                <p className="whitespace-pre-wrap break-words">{m.message}</p>
                                <div
                                  className={`flex items-center justify-end gap-1 mt-1 text-[10px] ${
                                    isMine ? 'text-slate-900/80' : 'text-slate-400'
                                  }`}
                                >
                                  <span>{formatMessageTime(m.createdAt)}</span>
                                  {isMine &&
                                    (m.isRead ? (
                                      <span title="Read" className="inline-flex items-center">
                                        <CheckCheck className="w-3.5 h-3.5 text-slate-950" />
                                      </span>
                                    ) : (
                                      <span title="Sent" className="inline-flex items-center">
                                        <Check className="w-3.5 h-3.5 text-slate-900/70" />
                                      </span>
                                    ))}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={privateChatEndRef} />
                    </div>

                    {/* Emoji Bar */}
                    {showPrivateEmojis && (
                      <div className="px-4 py-2 bg-slate-950 border-t border-slate-800 flex items-center gap-2 flex-wrap">
                        {QUICK_EMOJIS.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => setPrivateInput((prev) => prev + emoji)}
                            className="text-base hover:scale-125 transition-transform"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Message Input */}
                    <form
                      onSubmit={handleSendPrivateMessage}
                      className="p-3 bg-slate-950 border-t border-slate-800 flex items-center gap-2"
                    >
                      <button
                        type="button"
                        onClick={() => setShowPrivateEmojis((v) => !v)}
                        className="p-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-amber-400 transition-colors"
                      >
                        <Smile className="w-4 h-4" />
                      </button>
                      <input
                        type="text"
                        maxLength={500}
                        value={privateInput}
                        onChange={(e) => setPrivateInput(e.target.value)}
                        placeholder={`Message ${selectedFriend.displayName || selectedFriend.username}...`}
                        className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs sm:text-sm text-white focus:outline-none focus:border-amber-500"
                      />
                      <button
                        type="submit"
                        disabled={!privateInput.trim() || sendingPrivate}
                        className="px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-950 font-bold text-xs sm:text-sm flex items-center gap-1.5 transition-all"
                      >
                        <Send className="w-4 h-4" />
                        <span className="hidden sm:inline">Send</span>
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ENVIRONMENT 2: GLOBAL CHAT — PUBLIC COMMUNITY ENVIRONMENT */}
      {activeEnvironment === 'global_chat' && (
        <div className="bg-slate-900/90 border border-slate-800 rounded-2xl flex flex-col overflow-hidden h-[660px]">
          {/* Global Chat Header */}
          <div className="p-4 bg-slate-950/90 border-b border-slate-800 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
                <Globe className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm sm:text-base font-bold text-white">
                    Quick!Bible Global Fellowship Room
                  </h3>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-bold">
                    <Circle className="w-2 h-2 fill-emerald-400 text-emerald-400 animate-pulse" />
                    {onlineCount} Online
                  </span>
                </div>
                <p className="text-[11px] text-slate-400">
                  Public community room • Keep fellowship respectful & edifying (Max 300 chars)
                </p>
              </div>
            </div>

            {isAdmin && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-bold">
                <Shield className="w-3.5 h-3.5" /> Admin Moderation Active
              </span>
            )}
          </div>

          {/* Global Messages Stream */}
          <div
            ref={globalScrollContainerRef}
            onScroll={(e) => {
              const el = e.currentTarget;
              const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 90;
              setIsScrolledUpGlobal(!nearBottom);
              if (nearBottom) setUnreadGlobalCount(0);
            }}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-950/30 relative"
          >
            {loadingGlobalChat ? (
              <div className="text-center py-12 text-xs text-slate-500">Loading Global Chat...</div>
            ) : globalMessages.length === 0 ? (
              <div className="text-center py-12 text-xs text-slate-400">
                Welcome to Global Chat! Be the first to greet the community.
              </div>
            ) : (
              globalMessages.map((msg) => {
                const isOwn = msg.senderUid === userUid;
                const canDelete = isAdmin || isOwn;
                return (
                  <div
                    key={msg.id}
                    className={`group flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                      isOwn
                        ? 'bg-indigo-950/25 border-indigo-500/30'
                        : 'bg-slate-900/80 border-slate-800/80 hover:border-slate-700'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (!isRegisteredAccount) {
                          onRequireAuth?.('auth_signin');
                          return;
                        }
                        setInspectedUserUid(msg.senderUid);
                      }}
                      className="w-9 h-9 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-bold text-amber-400 shrink-0 overflow-hidden"
                    >
                      {msg.senderAvatar ? (
                        <img
                          src={msg.senderAvatar}
                          alt={msg.senderDisplayName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        (msg.senderDisplayName || msg.senderUsername || 'S').charAt(0).toUpperCase()
                      )}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <button
                          type="button"
                          onClick={() => {
                            if (!isRegisteredAccount) {
                              onRequireAuth?.('auth_signin');
                              return;
                            }
                            setInspectedUserUid(msg.senderUid);
                          }}
                          className="text-xs sm:text-sm font-bold text-white hover:text-amber-400 transition-colors"
                        >
                          {msg.senderDisplayName || msg.senderUsername}
                        </button>
                        <span className="text-[11px] text-slate-500">@{msg.senderUsername}</span>
                        {msg.senderRankTier && (
                          <BiblicalRankBadge rankName={msg.senderRankTier} size="sm" showLabel />
                        )}
                        <span className="text-[11px] text-slate-500 ml-auto">
                          {formatMessageTime(msg.createdAt)}
                        </span>
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => handleDeleteGlobalMessage(msg.id)}
                            title={isAdmin ? 'Admin: Delete Message' : 'Delete My Message'}
                            className="opacity-70 group-hover:opacity-100 p-1 rounded hover:bg-rose-500/20 text-slate-500 hover:text-rose-400 transition-all"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-slate-200 whitespace-pre-wrap break-words leading-relaxed">
                        {msg.message}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={globalChatEndRef} />
          </div>

          {/* Scroll to Latest / Unread Indicator */}
          {isScrolledUpGlobal && (
            <div className="px-4 py-1.5 bg-slate-950/90 border-t border-slate-800 flex justify-center">
              <button
                type="button"
                onClick={() => {
                  setIsScrolledUpGlobal(false);
                  setUnreadGlobalCount(0);
                  scrollGlobalContainerToBottom(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-500 text-white text-xs font-bold shadow-lg"
              >
                <ArrowDown className="w-3.5 h-3.5" />
                {unreadGlobalCount > 0
                  ? `${unreadGlobalCount} New Message${unreadGlobalCount > 1 ? 's' : ''} • Jump to Latest`
                  : 'Scroll to Latest'}
              </button>
            </div>
          )}

          {/* Emoji Picker Row */}
          {showGlobalEmojis && isRegisteredAccount && (
            <div className="px-4 py-2 bg-slate-950 border-t border-slate-800 flex items-center gap-2.5 flex-wrap">
              {QUICK_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => setGlobalInput((prev) => (prev + emoji).slice(0, 300))}
                  className="text-base hover:scale-125 transition-transform"
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          {/* Global Chat Input */}
          {!isRegisteredAccount ? (
            <div className="p-4 bg-slate-950 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-300">
                <Lock className="w-4 h-4 text-amber-400 shrink-0" />
                <span>Sign in or create a free account to post in Global Chat.</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onRequireAuth?.('auth_signup')}
                  className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs"
                >
                  Create Account
                </button>
                <button
                  onClick={() => onRequireAuth?.('auth_signin')}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-xs border border-slate-700"
                >
                  Sign In
                </button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSendGlobalMessage}
              className="p-3 bg-slate-950 border-t border-slate-800 flex items-center gap-2"
            >
              <button
                type="button"
                onClick={() => setShowGlobalEmojis((v) => !v)}
                className="p-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-amber-400 transition-colors"
              >
                <Smile className="w-4 h-4" />
              </button>
              <div className="relative flex-1">
                <input
                  type="text"
                  maxLength={300}
                  value={globalInput}
                  onChange={(e) => setGlobalInput(e.target.value)}
                  placeholder="Share a message with the global Quick!Bible community..."
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-3.5 pr-14 py-2.5 text-xs sm:text-sm text-white focus:outline-none focus:border-indigo-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500">
                  {globalInput.length}/300
                </span>
              </div>
              <button
                type="submit"
                disabled={!globalInput.trim() || sendingGlobal}
                className="px-4 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 disabled:opacity-40 text-white font-bold text-xs sm:text-sm flex items-center gap-1.5 transition-all"
              >
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">Post</span>
              </button>
            </form>
          )}
        </div>
      )}

      {/* User Profile Modal */}
      {inspectedUserUid && (
        <UserProfileModal
          isOpen={Boolean(inspectedUserUid)}
          targetUserUidOrUsername={inspectedUserUid}
          currentUserUid={userUid}
          onClose={() => setInspectedUserUid(null)}
        />
      )}
    </div>
  );
};

export default FriendsAndChatView;
